import {
  useGetCard, useGetEvent, useGetFace, useGetLists,
} from '@vlabs/api/artemy/client';
import { getArtemySampleUrl, proxyArtemyUrl } from '@vlabs/api/axios/point2-client';
import { DetectionIcon } from '@vlabs/icons';
import type { TEventReferencePreview } from '@vlabs/monokit/entities/matcher/types/types';
import { StreamLinkPoint3 } from '@vlabs/monokit/shared/ui/stream-link/StreamLink.point3';
import { routes } from '@vlabs/shared/navigation/routes';
import { formatDateTime } from '@vlabs/shared/utils/date';
import { FaceSample, Tag, Control } from '@vlabs/uikit';
import { buildLocationString } from '@vlapps/point/src/utils/location';
import { round } from 'lodash';
import { generatePath } from 'react-router-dom';

import st from './EventPreview.module.sass';
import { useSearchPageTranslation } from '../../../../i18n';

export interface EventPreviewPoint3Props {
  reference: TEventReferencePreview;
}

export function EventPreviewPoint3({ reference }: EventPreviewPoint3Props): JSX.Element {
  const { t } = useSearchPageTranslation();

  const { data: referenceEvent } = useGetEvent(reference?.event_id, { target: ['face_detections', 'top_match', 'location', 'create_time', 'stream_id'] });
  const location = buildLocationString(referenceEvent);
  const referenceSampleId = referenceEvent?.face_detections?.find(({ sample_id }) => !!sample_id)?.sample_id;
  const referenceFaceDetectionUrl = getArtemySampleUrl('faces', referenceSampleId);

  const topMatch = referenceEvent?.top_match;
  const hasTopMatch = !!topMatch;
  const faceTopMatch = topMatch && 'face_id' in topMatch ? topMatch : undefined;
  const matchFaceId = faceTopMatch?.face_id ?? undefined;
  const topMatchSimilarity = typeof topMatch?.similarity === 'number' ? round(topMatch.similarity * 100, 2) : '-';

  const { data: matchFace } = useGetFace(matchFaceId ?? '', undefined, {
    query: { enabled: !!matchFaceId },
  });
  const cardId = matchFace?.card_id;
  const { data: matchCard } = useGetCard(cardId ?? '', {}, {
    query: { enabled: !!cardId },
  });
  const mainFaceId = matchCard?.main_face_id;
  const { data: mainFace } = useGetFace(mainFaceId ?? '', undefined, {
    query: { enabled: !!mainFaceId },
  });
  const matchFaceUrl = proxyArtemyUrl(mainFace?.avatar);

  const cardListIds = matchCard?.lists ?? [];
  const { data: listsData } = useGetLists({ list_ids: cardListIds }, {
    query: { enabled: cardListIds.length > 0 },
  });
  const matchLists = listsData?.lists ?? [];

  return (
    <div
      className={st.Root}
      data-testid="table.referencesPreview"
    >
      <div
        className={st.TopSection}
        data-testid="eventPreviewTopSection"
      >
        <FaceSample
          className={st.Sample}
          src={referenceFaceDetectionUrl}
        />
        {!!topMatch && (
          <FaceSample
            className={st.Sample}
            src={matchFaceUrl}
          />
        )}
        <div className={st.Info}>
          <div className={st.Badges}>
            <Tag
              kind="neutral"
              value={t('reference_selector.event_preview.top_section.badge.label', { context: hasTopMatch ? '' : 'detection' })}
            />
            {!!topMatch && (
              <Tag
                className={st.DetectionTag}
                icon={<DetectionIcon />}
                kind="positive"
                value={t('reference_selector.event_preview.top_section.similarity_tag.label', { value: topMatchSimilarity })}
              />
            )}
          </div>
          <div className={st.Date}>{formatDateTime(referenceEvent?.create_time)}</div>
          <StreamLinkPoint3
            hideId
            kind="secondary"
            placeholders={{
              removed: t('reference_selector.event_preview.videostream.removed'),
              unavailable: t('reference_selector.event_preview.videostream.unavailable'),
            }}
            size="medium"
            streamId={referenceEvent?.stream_id}
          />
        </div>
      </div>
      <div className={st.BottomSection}>
        <div className={st.Location}>{location}</div>
        <div>
          <span className={st.Lists}>{t('reference_selector.event_preview.lists')}</span>
          {matchLists.length > 0 && matchLists.map((list) => (
            <Control.Link
              key={list?.list_id}
              kind="secondary"
              // @ts-expect-error требуется типизация routes
              to={generatePath(routes.lists.read, { listId: list?.list_id })}
            >
              {list?.user_data ?? list?.list_id}
            </Control.Link>
          ))}
          {matchLists.length === 0 && ' - '}
        </div>
      </div>
    </div>
  );
}
